// =============================================================================
// Wave 3.37 — PacketImageCard
// -----------------------------------------------------------------------------
// Reusable image display + edit panel for raw-material packet detail pages
// (Diamonds / Color Stones / Pearls). Wraps the lower-level ImageUploadInput
// with an inline "save / cancel" affordance and a public read-only view.
//
// Props:
//   • image         — current image data URL (or null/empty)
//   • canEdit       — boolean (parent decides; usually role-gated)
//   • onSave(url)   — async; called with the new image data URL OR null to clear
//   • title         — optional override (default 'Packet Image')
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import { ImageThumb } from '@/components/ImageThumb';
import { toast } from 'sonner';

export function PacketImageCard({ image, canEdit, onSave, title = 'Packet Image' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(image || '');
  const [saving, setSaving] = useState(false);

  // Keep the draft in sync when the parent reloads with a fresh image.
  useEffect(() => { setDraft(image || ''); }, [image]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Empty string means "clear the image".
      await onSave(draft || null);
      setEditing(false);
    } catch (e) {
      // Parent shows the toast; we just stay in edit mode so the user can retry.
      toast.error(e?.response?.data?.detail || 'Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAndSave = async () => {
    setSaving(true);
    try {
      await onSave(null);
      setDraft('');
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 mb-4" data-testid="packet-image-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[var(--ink-navy)]">{title}</div>
        {canEdit && !editing && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setEditing(true)}
              data-testid="packet-image-edit-button"
            >
              <Pencil size={12} className="mr-1.5" /> {image ? 'Replace' : 'Add Image'}
            </Button>
            {image && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={handleClearAndSave}
                disabled={saving}
                data-testid="packet-image-remove-button"
              >
                <Trash2 size={12} className="mr-1.5" /> Remove
              </Button>
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-3">
          <ImageUploadInput
            value={draft}
            onChange={setDraft}
            helpText="Max 2 MB. PNG / JPG recommended."
            testIdPrefix="packet-image-editor"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-8 luxury-button"
              onClick={handleSave}
              disabled={saving}
              data-testid="packet-image-save-button"
            >
              {saving ? 'Saving…' : 'Save Image'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => { setEditing(false); setDraft(image || ''); }}
              disabled={saving}
              data-testid="packet-image-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : image ? (
        <div className="flex items-center gap-4">
          <ImageThumb src={image} alt="Packet" size={120} title="Packet image" testId="packet-image-detail" />
          <div className="text-xs text-slate-500">Click the thumbnail to enlarge.</div>
        </div>
      ) : (
        <div className="text-sm text-slate-500" data-testid="packet-image-empty">No image uploaded for this packet.</div>
      )}
    </Card>
  );
}

export default PacketImageCard;
